import {
  AvailableUpgrade,
  Currency,
  stripeAmountToMajor,
  UpgradeOptionsResponse,
} from "@jammable/clientapi/src/payments";
import { Button, cn } from "@jammable/ui-core";
import { FaMusic, FaStar } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import { BiInfinite } from "react-icons/bi";
import { SiTiktok } from "react-icons/si";
import { CreditCheckout } from "../../../../../../../apps/web/components/modals/CreditCheckoutModal";
import { Spinner } from "@jammable/ui-components/Loader";
import { formatPriceWithLocale } from "../../../../../../../apps/web/utils/pricing";
import { FeatureHighlightItem } from "./FeatureHighlightItem";
import { useEffect, useMemo } from "react";

interface MobilePlanSelectorProps {
  upgradeOptions?: UpgradeOptionsResponse["availableUpgrades"];
  currency?: Currency;
  handleNewSubscription: () => void;
  showCheckout?: boolean;
  checkoutParams?: Record<string, unknown>;
  isUpgrading?: boolean;
  isLoadingPrices?: boolean;
  message?: string;
  handleSelectPlan?: (value: AvailableUpgrade) => void;
}

export function MobilePlanSelector({
  upgradeOptions,
  currency,
  handleNewSubscription,
  showCheckout,
  checkoutParams,
  isUpgrading,
  isLoadingPrices,
  message,
  handleSelectPlan,
}: MobilePlanSelectorProps) {
  // Get the creator plan or first available plan with lowest amount
  const plan: AvailableUpgrade | undefined =
    upgradeOptions
      ?.filter((p) => p?.type === "basic")
      .sort((a, b) => a?.amount - b?.amount)[1] || upgradeOptions?.[0];

  useEffect(() => {
    if (plan) {
      handleSelectPlan?.(plan);
    }
  }, [plan, handleSelectPlan]);
  // Memoize styles for animated elements
  const starStyles = useMemo(() => {
    return [...Array(30)].map(() => ({
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      opacity: Math.random() * 0.8 + 0.2,
      animationDelay: `${Math.random() * 3}s`,
      animationDuration: `${Math.random() * 3 + 2}s`,
    }));
  }, []);

  const musicNoteStyles = useMemo(() => {
    return [...Array(5)].map((_, i) => ({
      fontSize: `${Math.random() * 30 + 20}px`,
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      animationDelay: `${i * 0.5}s`,
      animationDuration: `${Math.random() * 10 + 10}s`,
    }));
  }, []);

  // Early return for checkout view
  if (showCheckout && checkoutParams) {
    return (
      <div className="tw-w-full tw-p-5 md:tw-min-w-[400px] ">
        <h2 className="tw-m-0 tw-mb-4 tw-text-3xl tw-font-black">Checkout</h2>
        <div>
          <CreditCheckout newLayout {...checkoutParams} />
        </div>
      </div>
    );
  }

  console.log(upgradeOptions);

  // The rest of the component renders the main UI shell
  return (
    <div className="tw-relative tw-bg-black tw-overflow-hidden md:tw-rounded-2xl md:tw-w-max md:tw-p-6 ">
      {/* Animated Background */}
      <div className="tw-absolute tw-inset-0">
        {/* Gradient Orbs */}
        <div className="tw-absolute tw-top-0 tw-left-1/4 tw-w-96 tw-h-96 tw-bg-purple-600 tw-rounded-full tw-filter tw-blur-[128px] tw-opacity-30 tw-animate-pulse"></div>
        <div
          className="tw-absolute tw-bottom-0 tw-right-1/4 tw-w-96 tw-h-96 tw-bg-pink-600 tw-rounded-full tw-filter tw-blur-[128px] tw-opacity-30 tw-animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>

        {/* Particles/Stars */}
        <div className="tw-absolute tw-inset-0">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="tw-absolute tw-w-1 tw-h-1 tw-bg-white tw-rounded-full tw-animate-pulse"
              style={starStyles[i]}
            />
          ))}
        </div>

        {/* Musical Notes Floating Animation */}
        <div className="tw-absolute tw-inset-0 tw-overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <FaMusic
              key={i}
              className="tw-absolute tw-text-purple-400 tw-opacity-20 tw-animate-float"
              style={musicNoteStyles[i]}
            />
          ))}
        </div>
      </div>

      {/* Content Area: Conditionally renders based on loading/plan state */}
      <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-6">
        {isLoadingPrices ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-p-40">
            <Spinner className="tw-border-white tw-border-5" size={6} />
          </div>
        ) : !plan ? (
          <p className="tw-text-2xl tw-text-gray-200 tw-font-bold tw-mt-0 tw-mb-1">
            No subscription plans available.
          </p>
        ) : (
          <>
            {/* Logo/Brand Section */}
            <div className=" tw-text-center tw-mb-4">
              <p className="tw-text-2xl tw-text-gray-200 tw-font-bold tw-mt-0 tw-mb-1">
                {message || "Play with music for the first time."}
              </p>

              {/* TikTok Badge */}
              <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-black/30 tw-backdrop-blur-xl tw-rounded-full tw-px-3 tw-py-1 tw-border tw-border-white/10 tw-mt-2">
                <SiTiktok className="tw-text-pink-400 tw-text-sm" />
                <span className="tw-text-gray-300 tw-text-xs tw-font-medium">
                  As seen on TikTok • 10B+ Views
                </span>
              </div>
            </div>

            {/* Feature Highlights */}
            <div className="tw-mb-6 tw-space-y-4 tw-w-full tw-max-w-sm">
              <FeatureHighlightItem
                icon={<BiInfinite className="tw-text-xl" />}
                text="Unlimited AI Covers"
              />
              <FeatureHighlightItem
                icon={<FaStar className="tw-text-xl" />}
                text="Full Voice Library (60,000+ voices)"
              />
              <FeatureHighlightItem
                icon={<HiSparkles className="tw-text-xl" />}
                text="Text to Speech"
              />
            </div>

            {/* Pricing Card */}
            <div className="tw-w-full tw-max-w-sm tw-mb-4">
              <div className="tw-relative tw-bg-white/5 tw-backdrop-blur-xl tw-rounded-3xl tw-p-6 tw-border tw-border-white/10 tw-border-solid">
                {/* Price Display */}
                <div className="tw-text-center tw-mb-4">
                  {plan.percentageDiscount && (
                    <div className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-purple-500/20 tw-text-purple-300 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold tw-mb-5">
                      <HiSparkles className="tw-text-xs" />
                      {plan.percentageDiscount}% off first month
                    </div>
                  )}

                  <div className="tw-flex tw-items-baseline tw-justify-center tw-gap-2">
                    {plan.discountedAmount &&
                      plan.discountedAmount !== plan.amount && (
                        <span className="tw-text-xl tw-text-white/40 tw-line-through">
                          {formatPriceWithLocale(
                            stripeAmountToMajor(plan.amount, currency!),
                            currency!,
                          )}
                        </span>
                      )}
                    <span className="tw-text-5xl tw-font-black tw-text-white">
                      {formatPriceWithLocale(
                        stripeAmountToMajor(
                          plan.discountedAmount || plan.amount,
                          currency!,
                        ),
                        currency!,
                      )}
                    </span>
                    <span className="tw-text-white/40 tw-text-lg">
                      /{plan.plan.interval}
                    </span>
                  </div>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={handleNewSubscription}
                  disabled={isUpgrading || isLoadingPrices}
                  className={cn(
                    "tw-w-full tw-h-14 tw-rounded-3xl tw-font-black tw-text-lg tw-tracking-wider",
                    "tw-bg-gradient-to-r tw-from-purple-600 tw-to-pink-600",
                    "tw-text-white tw-border-0",
                    "tw-shadow-xl hover:tw-shadow-2xl hover:tw-shadow-purple-500/25",
                    "tw-transform tw-transition-all tw-duration-300",
                    "hover:tw-scale-[1.02] active:tw-scale-[0.98]",
                    "disabled:tw-opacity-50 disabled:tw-cursor-not-allowed",
                    "tw-relative tw-overflow-hidden",
                  )}
                >
                  <span className="tw-relative tw-z-10">
                    {isUpgrading ? (
                      <Spinner
                        className="tw-border-white tw-border-3"
                        size={5}
                      />
                    ) : (
                      "TRY NOW"
                    )}
                  </span>
                  {/* Button Glow Effect */}
                  <div className="tw-absolute tw-inset-0 tw-bg-white tw-opacity-0 hover:tw-opacity-20 tw-transition-opacity tw-duration-300"></div>
                </Button>
              </div>
            </div>

            {/* Social Proof */}
            <div className="tw-text-center tw-text-gray-400">
              <div className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-mb-2">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="tw-text-yellow-400" />
                ))}
              </div>
              <div className="tw-text-sm">
                Loved by{" "}
                <span className="tw-font-bold tw-text-white">6,000,000+</span>{" "}
                creators
              </div>
            </div>
          </>
        )}
      </div>

      {/* CSS for floating animation */}
      <style>
        {`@keyframes float {
          0%,
          100% {
            transform: translateY(0) translateX(0) rotate(0deg);
          }
          25% {
            transform: translateY(-20px) translateX(10px) rotate(5deg);
          }
          50% {
            transform: translateY(-40px) translateX(-10px) rotate(-5deg);
          }
          75% {
            transform: translateY(-20px) translateX(5px) rotate(3deg);
          }
        }

        .tw-animate-float {
          animation: float linear infinite;
        }`}
      </style>
    </div>
  );
}
